import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  ArrowPathIcon,
  ScissorsIcon,
  CheckCircleIcon,
  DocumentIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  XMarkIcon,
  MagnifyingGlassPlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline';
import * as pdfjsLib from 'pdfjs-dist';
import { PDFDocument } from 'pdf-lib';
import toast from 'react-hot-toast';

// pdf.js needs a Web Worker for parsing. Pointing at the CDN keeps CRA's
// webpack config out of the picture.
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

interface PageThumb {
  page: number;
  dataUrl: string;
}

// Red, green, amber cycle. Adjacent invoices never share a color because
// consecutive indices always produce different mod-3 values.
interface GroupColor {
  ring: string;
  panelBg: string;
  panelText: string;
  badgeBg: string;
  badgeText: string;
}
const GROUP_HUES = [
  { hue: 0 },   // red
  { hue: 140 }, // green
  { hue: 45 },  // amber
];
const groupColorForIndex = (idx: number): GroupColor => {
  const { hue } = GROUP_HUES[idx % GROUP_HUES.length];
  return {
    ring: `hsl(${hue} 72% 42%)`,
    panelBg: `hsl(${hue} 75% 95%)`,
    panelText: `hsl(${hue} 70% 28%)`,
    badgeBg: `hsl(${hue} 78% 88%)`,
    badgeText: `hsl(${hue} 70% 22%)`,
  };
};

type ItemStatus = 'queued' | 'loading' | 'prompt' | 'splitter' | 'done';

interface QueueItem {
  id: string;
  file: File;
  status: ItemStatus;
  pageCount: number;
  thumbs: PageThumb[];
  extraBoundaries: Set<number>;
  splitInto: number;
  resultFiles: File[];
  error?: string;
}

const newItem = (file: File): QueueItem => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  file,
  status: 'queued',
  pageCount: 0,
  thumbs: [],
  extraBoundaries: new Set(),
  splitInto: 1,
  resultFiles: [],
});

// Split a PDF client-side using pdf-lib. Each entry in pageRanges becomes
// its own File. If pageRanges is empty or has a single all-pages range,
// the original file is returned untouched to keep the byte-for-byte match
// the OCR pipeline expects for a "process as one invoice" case.
async function splitPdfClientSide(file: File, pageRanges: number[][]): Promise<File[]> {
  if (pageRanges.length <= 1) return [file];
  const buf = await file.arrayBuffer();
  const src = await PDFDocument.load(buf);
  const stem = file.name.replace(/\.pdf$/i, '');
  const out: File[] = [];
  for (let i = 0; i < pageRanges.length; i++) {
    const dst = await PDFDocument.create();
    const zeroBased = pageRanges[i].map((p) => p - 1);
    const copied = await dst.copyPages(src, zeroBased);
    copied.forEach((p) => dst.addPage(p));
    const bytes = await dst.save();
    out.push(new File([bytes], `${stem}_invoice${i + 1}.pdf`, { type: 'application/pdf' }));
  }
  return out;
}

interface Props {
  files: File[];
  onComplete: (resultFiles: File[]) => void;
  onCancel: () => void;
}

const MAX_CONCURRENT_LOADS = 3;

const MultiInvoiceSplitter: React.FC<Props> = ({ files, onComplete, onCancel }) => {
  const [queue, setQueue] = useState<QueueItem[]>(() => files.map(newItem));
  const [activeId, setActiveId] = useState<string | null>(null);
  const loadingRef = useRef<Set<string>>(new Set());
  const completedRef = useRef(false);
  // High-res preview state. Rendered on demand at a bigger scale than the
  // grid thumbnails so the user can actually read the page. Cached per
  // (itemId, page) so re-opens are instant.
  const [previewPage, setPreviewPage] = useState<number | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewCache, setPreviewCache] = useState<Map<string, string>>(new Map());

  const active = useMemo(() => queue.find((q) => q.id === activeId) || null, [queue, activeId]);

  const patchItem = (id: string, patch: Partial<QueueItem>) => {
    setQueue((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };

  const loadItem = useCallback(async (id: string, file: File) => {
    if (loadingRef.current.has(id)) return;
    loadingRef.current.add(id);
    patchItem(id, { status: 'loading' });
    try {
      const buf = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
      const numPages = pdf.numPages;
      if (numPages <= 1) {
        patchItem(id, { status: 'done', pageCount: numPages, splitInto: 1, resultFiles: [file] });
        return;
      }
      const rendered: PageThumb[] = [];
      for (let n = 1; n <= numPages; n++) {
        const page = await pdf.getPage(n);
        const viewport = page.getViewport({ scale: 0.4 });
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) continue;
        await page.render({ canvasContext: ctx, viewport }).promise;
        rendered.push({ page: n, dataUrl: canvas.toDataURL('image/png') });
        patchItem(id, { thumbs: [...rendered], pageCount: numPages });
      }
      patchItem(id, { status: 'prompt' });
    } catch (e: any) {
      // If we can't parse the PDF at all, fall back to letting it flow
      // through as a single file, same as processing untouched. The
      // downstream OCR pipeline will decide what to do with it.
      patchItem(id, { status: 'done', error: e?.message || 'Failed to render PDF', splitInto: 1, resultFiles: [file] });
    } finally {
      loadingRef.current.delete(id);
    }
  }, []);

  // Load up to MAX_CONCURRENT_LOADS in parallel so single-page files
  // auto-complete while the user is busy with a multi-page one.
  useEffect(() => {
    const inFlight = queue.filter((q) => q.status === 'loading').length;
    const openSlots = MAX_CONCURRENT_LOADS - inFlight;
    if (openSlots <= 0) return;
    const nextUp = queue.filter((q) => q.status === 'queued').slice(0, openSlots);
    for (const it of nextUp) {
      loadItem(it.id, it.file);
    }
  }, [queue, loadItem]);

  // When the active item completes, clear activeId so the user picks the
  // next one from the sidebar themselves. No auto-jumping across files.
  useEffect(() => {
    if (!activeId) return;
    const cur = queue.find((q) => q.id === activeId);
    if (cur && cur.status === 'done') setActiveId(null);
  }, [queue, activeId]);

  // Emit onComplete once, when every item has finished.
  useEffect(() => {
    if (completedRef.current) return;
    if (queue.length === 0) return;
    if (queue.every((q) => q.status === 'done')) {
      completedRef.current = true;
      const flat = queue.flatMap((q) => q.resultFiles);
      onComplete(flat);
    }
  }, [queue, onComplete]);

  const groups: number[][] = useMemo(() => {
    if (!active || active.pageCount === 0) return [];
    const boundarySet = new Set(active.extraBoundaries);
    boundarySet.add(1);
    const sorted = Array.from(boundarySet)
      .filter((n) => n >= 1 && n <= active.pageCount)
      .sort((a, b) => a - b);
    const out: number[][] = [];
    for (let i = 0; i < sorted.length; i++) {
      const start = sorted[i];
      const end = i + 1 < sorted.length ? sorted[i + 1] - 1 : active.pageCount;
      const range: number[] = [];
      for (let p = start; p <= end; p++) range.push(p);
      out.push(range);
    }
    return out;
  }, [active]);

  const togglePageBoundary = (page: number) => {
    if (!active || page === 1) return;
    const next = new Set(active.extraBoundaries);
    if (next.has(page)) next.delete(page);
    else next.add(page);
    patchItem(active.id, { extraBoundaries: next });
  };

  // "Every page is its own invoice" shortcut. Common for scanned batches
  // where the clerk knows ahead of time each page is one invoice (vs
  // multi-page invoices that need manual boundary placement).
  const splitEveryPage = () => {
    if (!active) return;
    const every = new Set<number>();
    for (let p = 2; p <= active.pageCount; p++) every.add(p);
    patchItem(active.id, { extraBoundaries: every });
  };

  const clearBoundaries = () => {
    if (!active) return;
    patchItem(active.id, { extraBoundaries: new Set() });
  };

  // Render one page at a higher scale for the preview modal. Cached so
  // paging back and forth stays instant after the first render.
  const previewCacheKey = (itemId: string, page: number) => `${itemId}-${page}`;
  const ensurePreviewRendered = useCallback(async (itemId: string, file: File, page: number) => {
    const key = previewCacheKey(itemId, page);
    if (previewCache.has(key)) return;
    setPreviewLoading(true);
    try {
      const buf = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
      const pdfPage = await pdf.getPage(page);
      const viewport = pdfPage.getViewport({ scale: 1.5 });
      const canvas = document.createElement('canvas');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      await pdfPage.render({ canvasContext: ctx, viewport }).promise;
      const dataUrl = canvas.toDataURL('image/png');
      setPreviewCache((prev) => {
        const next = new Map(prev);
        next.set(key, dataUrl);
        return next;
      });
    } finally {
      setPreviewLoading(false);
    }
  }, [previewCache]);

  const openPreview = (page: number) => {
    if (!active) return;
    setPreviewPage(page);
    ensurePreviewRendered(active.id, active.file, page);
  };

  const closePreview = () => setPreviewPage(null);

  const navPreview = (delta: number) => {
    if (!active || previewPage == null) return;
    const next = previewPage + delta;
    if (next < 1 || next > active.pageCount) return;
    setPreviewPage(next);
    ensurePreviewRendered(active.id, active.file, next);
  };

  // Keyboard shortcuts while the preview modal is open: arrows to page,
  // space to toggle boundary, escape to close.
  useEffect(() => {
    if (previewPage == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { closePreview(); e.preventDefault(); }
      else if (e.key === 'ArrowLeft') { navPreview(-1); e.preventDefault(); }
      else if (e.key === 'ArrowRight') { navPreview(1); e.preventDefault(); }
      else if (e.key === ' ') {
        if (previewPage !== 1) togglePageBoundary(previewPage);
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewPage, active]);

  const finalizeAsSingle = async () => {
    if (!active) return;
    patchItem(active.id, { status: 'done', splitInto: 1, resultFiles: [active.file] });
  };

  const openSplitter = () => {
    if (!active) return;
    patchItem(active.id, { status: 'splitter' });
  };

  const confirmSplit = async () => {
    if (!active) return;
    const ranges = groups;
    try {
      const parts = await splitPdfClientSide(active.file, ranges);
      patchItem(active.id, { status: 'done', splitInto: parts.length, resultFiles: parts });
      toast.success(`${active.file.name}: split into ${parts.length}`);
    } catch (e: any) {
      toast.error(`Failed to split ${active.file.name}: ${e?.message || 'unknown error'}`);
      // Fall back to processing as one so we don't strand the file.
      patchItem(active.id, { status: 'done', splitInto: 1, resultFiles: [active.file] });
    }
  };

  const cancelAll = () => {
    onCancel();
  };

  const pendingCount = queue.filter((q) => q.status !== 'done').length;
  const awaitingDecisionCount = queue.filter((q) => q.status === 'prompt' || q.status === 'splitter').length;
  const doneCount = queue.filter((q) => q.status === 'done').length;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-7xl h-[90vh] flex overflow-hidden">
        {/* Sidebar mirrors the real workspace */}
        <div className="w-[280px] flex-shrink-0 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex flex-col">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-900 dark:text-white">Multi-page PDFs</span>
            <button
              type="button"
              onClick={cancelAll}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-gray-900 dark:hover:text-white"
              title="Cancel all, drop the whole batch"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
          <div className="flex border-b border-gray-200 dark:border-gray-700">
            <button className="flex-1 px-3 py-2 text-xs font-medium bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 border-b-2 border-brand-500">
              Pending ({pendingCount}){awaitingDecisionCount > 1 ? ` · ${awaitingDecisionCount} awaiting` : ''}
            </button>
            <button className="flex-1 px-3 py-2 text-xs text-gray-500 dark:text-gray-400" disabled>
              Done ({doneCount})
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {queue.map((it) => {
              const isActive = it.id === activeId;
              const clickable = it.status === 'prompt' || it.status === 'splitter';
              let icon: React.ReactNode;
              let label: string;
              if (it.status === 'queued') {
                icon = <ClockIcon className="h-4 w-4 text-gray-400" />;
                label = 'queued';
              } else if (it.status === 'loading') {
                icon = <div className="animate-spin rounded-full h-4 w-4 border-2 border-brand-500 border-t-transparent" />;
                label = `loading ${it.thumbs.length}${it.pageCount ? '/' + it.pageCount : ''}`;
              } else if (it.status === 'prompt') {
                icon = <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" />;
                label = `awaiting decision (${it.pageCount}p)`;
              } else if (it.status === 'splitter') {
                icon = <ScissorsIcon className="h-4 w-4 text-brand-500" />;
                label = 'marking boundaries';
              } else if (it.splitInto > 1) {
                icon = <ScissorsIcon className="h-4 w-4 text-emerald-500" />;
                label = `split into ${it.splitInto}`;
              } else {
                icon = <CheckCircleIcon className="h-4 w-4 text-emerald-500" />;
                label = 'ready';
              }
              return (
                <button
                  key={it.id}
                  type="button"
                  onClick={clickable ? () => setActiveId(it.id) : undefined}
                  disabled={!clickable}
                  className={`w-full text-left flex items-start gap-2 px-2 py-2 rounded-md transition ${
                    isActive
                      ? 'bg-brand-50 dark:bg-brand-900/20 ring-1 ring-brand-400 dark:ring-brand-600'
                      : clickable
                        ? 'hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer'
                        : 'cursor-default opacity-90'
                  }`}
                  title={clickable && !isActive ? 'Click to work on this file' : undefined}
                >
                  <div className="mt-0.5 flex-shrink-0">{icon}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-gray-900 dark:text-white truncate">{it.file.name}</div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400">{label}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            {!active && awaitingDecisionCount > 0 && (
              <div className="rounded-2xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-5">
                <div className="flex items-start gap-3">
                  <ExclamationTriangleIcon className="h-6 w-6 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-amber-900 dark:text-amber-200 mb-1">
                      {awaitingDecisionCount} multi-page file{awaitingDecisionCount === 1 ? '' : 's'} waiting for you
                    </div>
                    <div className="text-sm text-amber-800 dark:text-amber-300">
                      Pick one from the sidebar to decide whether it's one invoice or several.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!active && awaitingDecisionCount === 0 && pendingCount > 0 && (
              <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-8 text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-brand-500 border-t-transparent mx-auto mb-3" />
                <div className="text-sm font-semibold text-gray-800 dark:text-white">Loading pages...</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Multi-page files show up in the sidebar the moment they're ready to split.
                </div>
              </div>
            )}

            {active && active.status === 'loading' && (
              <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-8 text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-brand-500 border-t-transparent mx-auto mb-3" />
                <div className="text-sm font-semibold text-gray-800 dark:text-white">Loading {active.file.name}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Rendering page {active.thumbs.length}{active.pageCount ? ` of ${active.pageCount}` : ''}...
                </div>
              </div>
            )}

            {active && active.status === 'prompt' && (
              <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
                <div className="flex items-center gap-3 px-5 py-4 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700">
                  <DocumentIcon className="h-6 w-6 text-brand-600 dark:text-brand-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">{active.file.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{active.pageCount} pages detected</div>
                  </div>
                  <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />
                </div>
                <div className="px-5 py-5">
                  <div className="text-sm text-gray-800 dark:text-gray-200 font-medium mb-1">
                    This PDF has more than one page. Is it multiple invoices?
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                    Yes if this file contains 2 or more separate invoices you want treated individually. No if it is one invoice that just happens to be several pages.
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={finalizeAsSingle}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Process as one invoice
                    </button>
                    <button
                      type="button"
                      onClick={openSplitter}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-brand-600 hover:bg-brand-700 text-white rounded-md font-semibold"
                    >
                      <ScissorsIcon className="h-4 w-4" />
                      Yes, let me split it
                    </button>
                  </div>
                </div>
              </div>
            )}

            {active && active.status === 'splitter' && (
              <>
                <div className="mb-4 flex flex-wrap items-center gap-3 px-4 py-3 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                  <DocumentIcon className="h-5 w-5 text-brand-600 dark:text-brand-400" />
                  <div className="text-sm text-gray-800 dark:text-gray-200">
                    <span className="font-semibold">{active.file.name}</span>
                    <span className="ml-2 text-gray-500 dark:text-gray-400">
                      {active.pageCount} pages · splitting into {groups.length} invoice{groups.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    <button
                      type="button"
                      onClick={splitEveryPage}
                      disabled={active.pageCount <= 1 || active.extraBoundaries.size === active.pageCount - 1}
                      title="Make every page its own invoice. Useful for scanned batches of single-page invoices."
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ScissorsIcon className="h-3.5 w-3.5" />
                      Split every page
                    </button>
                    <button
                      type="button"
                      onClick={clearBoundaries}
                      disabled={active.extraBoundaries.size === 0}
                      title="Clear all breakpoints and treat the file as one invoice"
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div className="mb-3 text-xs text-gray-500 dark:text-gray-400 italic px-1">
                  Click any page from p2 onward to start a new invoice, or use the shortcuts above.
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                  <div className="lg:col-span-3 space-y-4">
                    {groups.map((groupPages, groupIdx) => {
                      const color = groupColorForIndex(groupIdx);
                      return (
                        <div
                          key={`group-${groupIdx}`}
                          className="rounded-xl border-2 p-3"
                          style={{ borderColor: color.ring, backgroundColor: color.panelBg }}
                        >
                          <div className="mb-2 flex items-center gap-2">
                            <span
                              className="text-sm font-bold px-2 py-0.5 rounded"
                              style={{ backgroundColor: color.badgeBg, color: color.badgeText }}
                            >
                              Invoice {groupIdx + 1}
                            </span>
                            <span className="text-xs text-gray-600 dark:text-gray-400">
                              {groupPages.length === 1
                                ? `page ${groupPages[0]}`
                                : `pages ${groupPages[0]}–${groupPages[groupPages.length - 1]}`}
                              {' '}({groupPages.length} page{groupPages.length === 1 ? '' : 's'})
                            </span>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3">
                            {groupPages.map((pageNum) => {
                              const thumb = active.thumbs.find((t) => t.page === pageNum);
                              if (!thumb) return null;
                              const isBoundary = pageNum === 1 || active.extraBoundaries.has(pageNum);
                              const isPageOne = pageNum === 1;
                              return (
                                <div
                                  key={pageNum}
                                  className="relative group"
                                  style={{ boxShadow: `0 0 0 2px ${color.ring}`, borderRadius: '0.5rem' }}
                                >
                                  <button
                                    type="button"
                                    onClick={() => togglePageBoundary(pageNum)}
                                    disabled={isPageOne}
                                    className={`block w-full rounded-lg overflow-hidden transition ${
                                      isPageOne
                                        ? 'cursor-not-allowed opacity-95'
                                        : 'hover:scale-[1.02] hover:shadow-lg cursor-pointer'
                                    }`}
                                    title={
                                      isPageOne
                                        ? 'Page 1 is always the start of the first invoice'
                                        : isBoundary
                                          ? 'Click to un-mark: this page will merge into the previous invoice'
                                          : 'Click to split this page into its own new invoice'
                                    }
                                  >
                                    <img src={thumb.dataUrl} alt={`page ${pageNum}`} className="w-full block bg-white" />
                                  </button>
                                  <div
                                    className="absolute top-1 left-1 text-[10px] font-bold px-1.5 py-0.5 rounded shadow-sm pointer-events-none"
                                    style={{ backgroundColor: color.badgeBg, color: color.badgeText }}
                                  >
                                    p{pageNum}
                                  </div>
                                  {isBoundary && !isPageOne && (
                                    <div
                                      className="absolute top-1 right-1 text-[10px] font-bold px-1.5 py-0.5 rounded shadow-sm inline-flex items-center gap-0.5 pointer-events-none"
                                      style={{ backgroundColor: color.badgeBg, color: color.badgeText }}
                                    >
                                      <ScissorsIcon className="h-3 w-3" />
                                      starts here
                                    </div>
                                  )}
                                  {/* Magnifier: opens a full-size preview so
                                      the clerk can actually read the page. */}
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); openPreview(pageNum); }}
                                    title="View this page full size"
                                    className="absolute bottom-1 right-1 h-7 w-7 rounded-full bg-white/90 dark:bg-gray-800/90 border border-gray-300 dark:border-gray-600 shadow-sm inline-flex items-center justify-center text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-700 hover:scale-110 transition"
                                  >
                                    <MagnifyingGlassPlusIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="lg:col-span-1">
                    <div className="sticky top-4 space-y-3">
                      <button
                        type="button"
                        onClick={confirmSplit}
                        disabled={groups.length === 0}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <CheckCircleIcon className="h-4 w-4" />
                        Confirm · move to next
                      </button>
                      <button
                        type="button"
                        onClick={finalizeAsSingle}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 text-xs"
                      >
                        Actually, treat as one invoice
                      </button>
                      {awaitingDecisionCount > 1 && (
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight text-center">
                          {awaitingDecisionCount - 1} more file{awaitingDecisionCount - 1 === 1 ? '' : 's'} waiting after this
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Page preview modal. Shown above the splitter modal (z higher).
          Full-size sharp render of the selected page, with prev/next
          navigation and the split/merge action in one place so the user
          can see the page BEFORE deciding to break it off. */}
      {previewPage != null && active && (() => {
        const isPageOne = previewPage === 1;
        const isBoundary = isPageOne || active.extraBoundaries.has(previewPage);
        const groupIdx = groups.findIndex((g) => g.includes(previewPage));
        const color = groupColorForIndex(groupIdx >= 0 ? groupIdx : 0);
        const cachedUrl = previewCache.get(previewCacheKey(active.id, previewPage));
        return (
          <div className="fixed inset-0 z-[60] bg-black/80 flex flex-col items-center justify-center p-4">
            {/* Top toolbar */}
            <div className="w-full max-w-6xl mb-3 flex items-center gap-3 text-white">
              <div className="flex-1">
                <div className="text-sm font-semibold truncate">{active.file.name}</div>
                <div className="text-xs text-gray-300">
                  Page {previewPage} of {active.pageCount}
                  {groupIdx >= 0 && (
                    <span
                      className="ml-2 px-2 py-0.5 rounded text-[10px] font-bold"
                      style={{ backgroundColor: color.badgeBg, color: color.badgeText }}
                    >
                      Invoice {groupIdx + 1}
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={closePreview}
                className="p-2 rounded-md hover:bg-white/10"
                title="Close (Esc)"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Big image */}
            <div className="flex-1 min-h-0 w-full max-w-6xl flex items-center justify-center relative bg-white rounded-lg overflow-hidden">
              {!cachedUrl || previewLoading ? (
                <div className="text-gray-500 text-sm">Loading preview...</div>
              ) : (
                <img
                  src={cachedUrl}
                  alt={`page ${previewPage}`}
                  className="max-h-full max-w-full object-contain"
                />
              )}
              {previewPage > 1 && (
                <button
                  type="button"
                  onClick={() => navPreview(-1)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/60 text-white hover:bg-black/80 flex items-center justify-center"
                  title="Previous page (←)"
                >
                  <ChevronLeftIcon className="h-6 w-6" />
                </button>
              )}
              {previewPage < active.pageCount && (
                <button
                  type="button"
                  onClick={() => navPreview(1)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/60 text-white hover:bg-black/80 flex items-center justify-center"
                  title="Next page (→)"
                >
                  <ChevronRightIcon className="h-6 w-6" />
                </button>
              )}
            </div>

            {/* Bottom action bar */}
            <div className="w-full max-w-6xl mt-3 flex items-center justify-center gap-2">
              {isPageOne ? (
                <div className="text-xs text-gray-300 italic px-3 py-2">
                  Page 1 is always the start of the first invoice
                </div>
              ) : isBoundary ? (
                <button
                  type="button"
                  onClick={() => togglePageBoundary(previewPage)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white text-gray-900 hover:bg-gray-100 text-sm font-semibold"
                  title="Space"
                >
                  <ArrowUturnLeftIcon className="h-4 w-4" />
                  Merge back into previous invoice
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => togglePageBoundary(previewPage)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold"
                  title="Space"
                >
                  <ScissorsIcon className="h-4 w-4" />
                  Start a new invoice at this page
                </button>
              )}
              <div className="ml-4 text-[11px] text-gray-400 hidden sm:block">
                ← → to navigate · Space to toggle · Esc to close
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default MultiInvoiceSplitter;
